import { Fragment, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

/**
 * Markdown als React-Elemente — ohne Fremdbibliothek und ohne HTML.
 *
 * Der Text stammt aus einem Eingabefeld im Backoffice. Ein Markdown-Umsetzer
 * von der Stange liefert HTML als Zeichenkette, und die kommt nur über
 * dangerouslySetInnerHTML in die Seite: Damit stünde zwischen dem Eingabefeld
 * eines Ladens und dem Browser jedes Kunden nur noch die Frage, ob ein
 * Bereiniger alles kennt. Hier gibt es diese Frage nicht — es entstehen
 * ausschließlich React-Elemente, und was der Text sonst enthält, ist Text.
 * Auch ein <script> ist dann nur eine Zeile, die so dasteht.
 *
 * Beherrscht werden die Auszeichnungen, die ein Impressum, eine
 * Widerrufsbelehrung und eine Seite „Über uns" brauchen: Überschriften,
 * Absätze, Aufzählungen mit und ohne Nummern, fett, kursiv, Code am Stück und
 * Verweise. Tabellen, Bilder und Fußnoten fehlen bewusst — wer sie hier
 * nachbaut, baut irgendwann doch die Bibliothek nach.
 */
export function Markdown({ text }: { text: string }) {
  return <>{bloecke(text)}</>
}

/** Zeilen, die einen Aufzählungspunkt beginnen. */
const PUNKT = /^\s{0,3}[-*+]\s+(.*)$/
const NUMMER = /^\s{0,3}\d{1,9}[.)]\s+(.*)$/
const UEBERSCHRIFT = /^\s{0,3}(#{1,6})\s+(.*)$/
const ZITAT = /^\s{0,3}>\s?(.*)$/

function bloecke(text: string): ReactNode[] {
  // Zeilenenden vereinheitlichen: Ein Ladeninhaber schreibt seine Seite unter
  // Windows genauso wie unter Linux, und \r am Zeilenende ließe jedes Muster
  // hier ins Leere laufen.
  const zeilen = text.replace(/\r\n?/g, '\n').split('\n')
  const ausgabe: ReactNode[] = []

  let i = 0
  while (i < zeilen.length) {
    // Der Zugriff ist abgesichert, weil tsconfig jede Indexierung als
    // möglicherweise leer behandelt — hier kann sie es nicht sein.
    const zeile = zeilen[i] ?? ''

    if (zeile.trim() === '') {
      i += 1
      continue
    }

    const ueberschrift = UEBERSCHRIFT.exec(zeile)
    if (ueberschrift) {
      // Die Überschrift der Seite steht als h1 schon darüber. Eine zweite wäre
      // für Vorleseprogramme ein zweiter Seitenanfang — deshalb beginnt der
      // Text bei h2, egal wie viele Rauten davorstehen.
      const stufe = Math.min(4, (ueberschrift[1] ?? '#').length + 1)
      const Tag = (['h2', 'h3', 'h4'] as const)[stufe - 2] ?? 'h4'
      ausgabe.push(<Tag key={ausgabe.length}>{zeichen(ueberschrift[2] ?? '')}</Tag>)
      i += 1
      continue
    }

    if (PUNKT.test(zeile) || NUMMER.test(zeile)) {
      const nummeriert = NUMMER.test(zeile)
      const muster = nummeriert ? NUMMER : PUNKT
      const punkte: string[] = []
      while (i < zeilen.length) {
        const treffer = muster.exec(zeilen[i] ?? '')
        if (!treffer) break
        punkte.push(treffer[1] ?? '')
        i += 1
      }
      const Liste = nummeriert ? 'ol' : 'ul'
      ausgabe.push(
        <Liste key={ausgabe.length}>
          {punkte.map((punkt, nummer) => (
            <li key={nummer}>{zeichen(punkt)}</li>
          ))}
        </Liste>,
      )
      continue
    }

    if (ZITAT.test(zeile)) {
      const teile: string[] = []
      while (i < zeilen.length) {
        const treffer = ZITAT.exec(zeilen[i] ?? '')
        if (!treffer) break
        teile.push(treffer[1] ?? '')
        i += 1
      }
      ausgabe.push(<blockquote key={ausgabe.length}>{absatz(teile.join('\n'))}</blockquote>)
      continue
    }

    // Alles Übrige ist ein Absatz: bis zur nächsten Leerzeile oder bis etwas
    // anfängt, das für sich steht.
    const absatzzeilen: string[] = []
    while (i < zeilen.length) {
      const naechste = zeilen[i] ?? ''
      if (
        naechste.trim() === '' ||
        UEBERSCHRIFT.test(naechste) ||
        PUNKT.test(naechste) ||
        NUMMER.test(naechste) ||
        ZITAT.test(naechste)
      ) {
        break
      }
      absatzzeilen.push(naechste.trim())
      i += 1
    }
    ausgabe.push(<p key={ausgabe.length}>{absatz(absatzzeilen.join('\n'))}</p>)
  }

  return ausgabe
}

/**
 * Ein Absatz mit harten Zeilenumbrüchen.
 *
 * Markdown fasst einfache Umbrüche sonst zu einer Zeile zusammen. Hier wird
 * aber kein Markdown-Dokument gesetzt, sondern das, was jemand in ein
 * mehrzeiliges Feld getippt hat — und die drei Zeilen einer Anschrift im
 * Impressum sind drei Zeilen, keine Prosa.
 */
function absatz(text: string): ReactNode[] {
  return text.split('\n').flatMap((zeile, nummer) =>
    nummer === 0
      ? [<Fragment key={nummer}>{zeichen(zeile)}</Fragment>]
      : [<br key={`br${nummer}`} />, <Fragment key={nummer}>{zeichen(zeile)}</Fragment>],
  )
}

/**
 * Die Auszeichnungen innerhalb einer Zeile.
 *
 * Das Muster entsteht bei jedem Aufruf neu: Es sucht global, merkt sich also
 * seine Stelle im Text — und diese Funktion ruft sich für den Inhalt von
 * **fett** selbst auf. Ein gemeinsames Muster verlöre dabei die Stelle des
 * Aufrufers.
 */
function zeichen(text: string): ReactNode[] {
  const muster =
    /(\*\*|__)([\s\S]+?)\1|(\*|_)([\s\S]+?)\3|`([^`]+)`|\[([^\]]*)\]\(([^()\s]*)\)/g
  const teile: ReactNode[] = []
  let gelesen = 0
  let treffer: RegExpExecArray | null

  while ((treffer = muster.exec(text)) !== null) {
    if (treffer.index > gelesen) teile.push(text.slice(gelesen, treffer.index))
    const schluessel = teile.length

    if (treffer[1] !== undefined) {
      teile.push(<strong key={schluessel}>{zeichen(treffer[2] ?? '')}</strong>)
    } else if (treffer[3] !== undefined) {
      teile.push(<em key={schluessel}>{zeichen(treffer[4] ?? '')}</em>)
    } else if (treffer[5] !== undefined) {
      teile.push(<code key={schluessel}>{treffer[5] ?? ''}</code>)
    } else {
      teile.push(verweis(treffer[6] ?? '', treffer[7] ?? '', schluessel, treffer[0]))
    }

    gelesen = treffer.index + treffer[0].length
  }

  if (gelesen < text.length) teile.push(text.slice(gelesen))
  return teile
}

/**
 * Ein Verweis — oder, wenn die Adresse nichts taugt, schlichter Text.
 *
 * Erlaubt sind http, https, mailto, tel und Adressen im eigenen Laden. Alles
 * andere fällt durch: „javascript:" in einem Verweis ist genau der Weg, den
 * ein Umsetzer ohne eigene Prüfung offen ließe. Eine unbrauchbare Adresse wird
 * nicht verschluckt, sondern steht als Text da — sonst sucht der Ladeninhaber
 * seinen verschwundenen Verweis.
 */
function verweis(beschriftung: string, adresse: string, schluessel: number, roh: string): ReactNode {
  const ziel = adresse.trim()

  // Im eigenen Laden bleibt es ein Wechsel ohne Neuladen: Ein Verweis von der
  // Widerrufsbelehrung zurück ins Sortiment soll den Warenkorb nicht wegwerfen.
  if (/^\/(?!\/)/.test(ziel)) {
    return (
      <Link key={schluessel} to={ziel}>
        {zeichen(beschriftung)}
      </Link>
    )
  }

  if (/^(https?:|mailto:|tel:|#)/i.test(ziel)) {
    const auswaerts = /^https?:/i.test(ziel)
    return (
      <a
        key={schluessel}
        href={ziel}
        // Fremde Ziele in einem neuen Fenster: Wer im Impressum auf die Seite
        // der Handelskammer klickt, soll nicht aus dem Laden herausfallen.
        {...(auswaerts ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {zeichen(beschriftung)}
      </a>
    )
  }

  return <Fragment key={schluessel}>{roh}</Fragment>
}
